import { Link as RouterLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyGuide() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Superbuy Spreadsheet Safety Guide: Secure Shopping and Privacy Protection',
    description: 'Comprehensive safety guide for Superbuy Spreadsheet users. Learn about website security, privacy protection, avoiding scams, recognizing fake sites, and responsible shopping practices.',
    datePublished: '2026-07-13',
    dateModified: '2026-07-13',
    author: { '@type': 'Organization', name: 'Superbuy Spreadsheet' },
    publisher: { '@type': 'Organization', name: 'Superbuy Spreadsheet' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Is my personal information safe when using Superbuy Spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. The Superbuy Spreadsheet platform does not require any personal information or account creation to browse categories and access guides. Your browsing activity is protected by HTTPS encryption, and no personal data is collected during your visit.',
        },
      },
      {
        '@type': 'Question',
        name: 'How can I tell if a website is the real Superbuy Spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Always verify you are visiting superbuyspread.casa — this is the only official domain. Look for the HTTPS padlock in your browser address bar. The genuine Superbuy Spreadsheet site will never ask for payment or sensitive personal information to access resources.',
        },
      },
      {
        '@type': 'Question',
        name: 'What should I do if I find a fake Superbuy Spreadsheet website?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'If you encounter a website impersonating Superbuy Spreadsheet, do not interact with it or provide any information. Navigate away immediately and ensure you are visiting the official domain at superbuyspread.casa. Report suspicious sites through standard browser reporting tools if possible.',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

        <article className="py-10 md:py-14">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">

            <div className="mb-8">
              <RouterLink to="/" className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium mb-6 whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </RouterLink>
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                Safety Guide
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                Superbuy Spreadsheet Safety Guide: Secure Shopping and Privacy Protection
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>10 min read</span>
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>Updated July 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-user-line"></i>Superbuy Spreadsheet Team</span>
              </div>
            </div>

            <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed">

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Why Online Shopping Safety Matters More Than Ever
                </h2>
                <p className="text-base mb-4">
                  As international online shopping continues to grow, so does the sophistication of bad actors looking to exploit unsuspecting shoppers. The <strong>Superbuy Spreadsheet</strong> platform takes security seriously — but informed users are the strongest line of defense. This guide walks you through everything you need to know about staying safe while using our platform and shopping online in general.
                </p>
                <p className="text-base mb-4">
                  Safety in online shopping is not just about avoiding obvious scams. It encompasses <strong>privacy protection, secure browsing habits, recognizing legitimate platforms, and making responsible purchasing decisions</strong>. Each of these elements plays a crucial role in ensuring your shopping experience is both enjoyable and secure.
                </p>
                <p className="text-base">
                  The good news is that the <RouterLink to="/" className="text-primary-600 hover:text-primary-700 font-medium">Superbuy Spreadsheet website</RouterLink> is designed with security as a foundational principle. From HTTPS encryption to a no-registration-required browsing model, every aspect of the platform prioritizes your safety without compromising on functionality.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Platform Security: How Superbuy Spreadsheet Protects You
                </h2>

                <div className="space-y-4">
                  {[
                    {
                      title: 'HTTPS Encryption',
                      desc: 'All data transmitted between your browser and the Superbuy Spreadsheet website is encrypted using industry-standard HTTPS protocol. Look for the padlock icon in your browser address bar to verify this protection is active during every visit.',
                      icon: 'ri-lock-line',
                    },
                    {
                      title: 'No Personal Data Collection',
                      desc: 'You can browse every category, read every guide, and use every comparison tool without creating an account or providing any personal information. This privacy-by-design approach means there is simply no personal data to be compromised.',
                      icon: 'ri-eye-off-line',
                    },
                    {
                      title: 'Clean Browsing Environment',
                      desc: 'The Superbuy Spreadsheet platform maintains a clean, ad-free browsing environment with no deceptive pop-ups, misleading redirects, or third-party trackers that could compromise your browsing security.',
                      icon: 'ri-shield-check-line',
                    },
                    {
                      title: 'No Payment Processing',
                      desc: 'All purchasing transactions occur on the main shopping website, not on the Superbuy Spreadsheet platform. We never request payment information because we never process payments — our role is purely informational and organizational.',
                      icon: 'ri-bank-card-line',
                    },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                      <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-accent-600 text-base`}></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground-800 text-sm mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Recognizing the Official Superbuy Spreadsheet Website
                </h2>
                <p className="text-base mb-4">
                  One of the most important safety skills is knowing how to verify you are on the genuine <strong>Superbuy Spreadsheet</strong> website. Imitation sites sometimes appear with similar-looking domain names designed to trick unsuspecting visitors into providing personal information or making payments for free resources.
                </p>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 my-6">
                  <h4 className="font-heading text-base font-semibold text-foreground-900 mb-3">Official Website Verification Checklist</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5"></i><span>The domain is exactly <strong>superbuyspread.casa</strong> — no variations, no hyphens, no different endings</span></li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5"></i><span>The URL begins with <strong>https://</strong> and shows a padlock icon in your browser</span></li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5"></i><span>You are never asked for payment or personal information to access resources</span></li>
                    <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5"></i><span>The site design is clean, professional, and consistent — imitation sites often have visual inconsistencies</span></li>
                  </ul>
                </div>
                <p className="text-base">
                  If you ever encounter a website claiming to be Superbuy Spreadsheet that does not match these criteria, navigate away immediately. Bookmark the official <RouterLink to="/" className="text-primary-600 hover:text-primary-700 font-medium">superbuyspread.casa</RouterLink> domain to ensure you always return to the genuine site.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Responsible Shopping Practices Every User Should Follow
                </h2>
                <p className="text-base mb-4">
                  Beyond platform security, responsible shopping habits protect you throughout the entire purchasing journey. These practices apply regardless of which platform you are using to discover products.
                </p>

                <div className="space-y-3 mb-4">
                  {[
                    { title: 'Review Product Details Thoroughly', desc: 'Before making any purchase, carefully review all available product information including specifications, sizing details, material descriptions, and any user feedback. Never rush into a purchase without understanding exactly what you are buying.' },
                    { title: 'Verify Seller Reputation', desc: 'When purchasing through any platform, take time to check the seller history and reputation. Established sellers with consistent positive feedback are generally safer choices than brand-new sellers with no track record.' },
                    { title: 'Understand Return and Refund Policies', desc: 'Know the return policy before you buy. Understanding what happens if a product does not meet your expectations protects you from unpleasant surprises and helps you make more confident purchasing decisions.' },
                    { title: 'Keep Records of Your Transactions', desc: 'Save order confirmations, payment receipts, and any communication with sellers. These records are invaluable if you ever need to resolve an issue or reference a past purchase.' },
                    { title: 'Use Secure Payment Methods', desc: 'Whenever possible, use payment methods that offer buyer protection. Credit cards and established payment processors typically provide dispute resolution services that can help if something goes wrong with a transaction.' },
                  ].map((item) => (
                    <div key={item.title} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <h4 className="font-semibold text-foreground-800 text-sm mb-1">{item.title}</h4>
                      <p className="text-sm text-foreground-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Common Online Shopping Scams and How to Avoid Them
                </h2>
                <p className="text-base mb-4">
                  Awareness is your best defense against online shopping scams. While the Superbuy Spreadsheet platform itself is safe and secure, understanding common scam patterns helps you stay protected across your entire online shopping experience.
                </p>
                <div className="overflow-x-auto rounded-xl border border-background-200/70 my-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left py-3 px-5 font-semibold text-foreground-800">Scam Type</th>
                        <th className="text-left py-3 px-5 font-semibold text-foreground-800">How It Works</th>
                        <th className="text-left py-3 px-5 font-semibold text-foreground-800">How to Avoid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Fake Websites', 'Imitating official sites to steal information', 'Always verify the domain name carefully'],
                        ['Too-Good-To-Be-True Prices', 'Luring buyers with unrealistically low prices', 'Compare prices across multiple sources'],
                        ['Phishing Messages', 'Fake emails or messages requesting login info', 'Never click suspicious links in messages'],
                        ['Fake Payment Requests', 'Asking for payment outside official channels', 'Only use official payment processors'],
                      ].map((row) => (
                        <tr key={row[0]} className="border-t border-background-200/70">
                          <td className="py-3 px-5 font-medium text-foreground-800">{row[0]}</td>
                          <td className="py-3 px-5 text-foreground-600">{row[1]}</td>
                          <td className="py-3 px-5 text-primary-700">{row[2]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-xl bg-primary-50 border border-primary-200">
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">Shop with Confidence</h3>
                    <p className="text-sm text-foreground-600">Your safety is our priority. Browse securely and shop responsibly with the Superbuy Spreadsheet platform.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <RouterLink to="/" className="px-6 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all duration-200 text-center">
                      Back to Home
                      <i className="ri-arrow-right-line ml-1.5"></i>
                    </RouterLink>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}